import { Link, useSearchParams } from 'react-router-dom';

export default function SignupTypeSelect() {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('inviteToken');
  const email = searchParams.get('email');
  const phone = searchParams.get('phone');
  const redirect = searchParams.get('redirect');

  // 초대 토큰이 있을 때 하위 페이지로 전달할 쿼리 파라미터 생성
  const buildQueryString = (additionalParams?: Record<string, string>) => {
    const params = new URLSearchParams();
    if (inviteToken) params.set('inviteToken', inviteToken);
    if (email) params.set('email', email);
    if (phone) params.set('phone', phone);
    if (redirect) params.set('redirect', redirect);
    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        params.set(key, value);
      });
    }
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* 헤더 */}
        <div className="text-center">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold text-brand-600 hover:text-brand-700">SafetyHub</h1>
          </Link>
          <h2 className="mt-4 text-2xl font-bold text-ink-900">무료로 시작하기</h2>
          <p className="mt-2 text-sm text-ink-600">
            이미 계정이 있으신가요?{' '}
            <Link to={redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login'} className="font-medium text-brand-600 hover:text-brand-500">
              로그인
            </Link>
          </p>
        </div>

        {/* 초대 알림 */}
        {inviteToken && (
          <div className="bg-green-50 border border-green-200 rounded-card p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">조직 초대가 있습니다</p>
                <p className="mt-1 text-sm text-green-700">
                  회원가입 완료 시 자동으로 초대가 수락됩니다.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 무료 혜택 안내 */}
        <div className="bg-blue-50 rounded-card p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-blue-800">무료 요금제로 시작</p>
              <ul className="mt-2 text-sm text-blue-700 space-y-1">
                <li>• 매월 2~3회 현장 점검</li>
                <li>• 사진 50장 자동 분석</li>
                <li>• 카드 등록 없이 바로 시작</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 가입 방법 선택 */}
        <div className="space-y-4">
          <p className="text-center text-sm text-ink-600">가입 방법을 선택해주세요</p>

          {/* 이메일로 가입 - 추천 */}
          <Link
            to={`/signup/email${buildQueryString()}`}
            className="block w-full p-4 border-2 border-brand-200 bg-brand-50 rounded-card hover:border-brand-500 hover:bg-brand-100 transition-colors"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-8 w-8 text-brand-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <div className="flex items-center">
                  <h3 className="text-lg font-medium text-ink-900">이메일로 가입</h3>
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-600 text-white">
                    추천
                  </span>
                </div>
                <p className="text-sm text-ink-500">
                  이메일 인증으로 안전하게 가입합니다
                </p>
              </div>
              <div className="ml-auto">
                <svg
                  className="h-5 w-5 text-ink-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          </Link>

          {/* 아이디로 가입 */}
          <Link
            to={`/signup/username${buildQueryString()}`}
            className="block w-full p-4 border-2 border-ink-200 rounded-card hover:border-brand-500 hover:bg-brand-50 transition-colors"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-8 w-8 text-ink-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-ink-900">아이디로 가입</h3>
                <p className="text-sm text-ink-500">
                  아이디를 직접 만들어 가입합니다
                </p>
              </div>
              <div className="ml-auto">
                <svg
                  className="h-5 w-5 text-ink-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          </Link>
        </div>

        {/* 약관 안내 */}
        <p className="text-center text-xs text-ink-500">
          가입 시{' '}
          <span className="text-brand-600">이용약관</span>
          {' '}및{' '}
          <span className="text-brand-600">개인정보처리방침</span>
          에 동의하게 됩니다.
        </p>

        {/* 푸터 */}
        <p className="mt-8 text-center text-xs text-ink-500">
          SafetyHub &copy; 2025. All rights reserved.
        </p>
      </div>
    </div>
  );
}
